import { FocusMonitor } from '@angular/cdk/a11y'
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, Optional, Self, SimpleChanges } from '@angular/core'
import { FormControl, FormGroupDirective, NgControl, NgForm } from '@angular/forms'
import { MatFormFieldControl } from '@angular/material/form-field'
import { ApiService, Procedure, ProcedureCodeModifier } from '@mmx/shared'
import { BehaviorSubject, catchError, combineLatest, concatMap, filter, map, Observable, of, ReplaySubject, shareReplay, startWith, tap } from 'rxjs'

import { CustomMaterialInputControlComponent } from '../../../custom-field'

@Component({
  selector: 'mmx-code-modifiers-select',
  templateUrl: './code-modifiers-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: CodeModifiersSelectComponent,
    },
  ],
  standalone: false,
})
export class CodeModifiersSelectComponent extends CustomMaterialInputControlComponent<string[]> implements OnChanges, OnDestroy {
  @Input()
  public code: string

  public modifiersControl = new FormControl<string[]>([])
  public filterControl = new FormControl<string>('')
  public procedureDataParams$ = new ReplaySubject<string>()
  public modifiersShown$ = new BehaviorSubject<'top' | 'supported'>('supported')

  public procedure$: Observable<Procedure> = this.procedureDataParams$.pipe(
    concatMap((code) => this.restService.getProcedureInfo(code, true)),
    catchError(() => {
      this.modifiersControl.disable()
      return of(null)
    }),
    shareReplay(1),
  )

  public topModifiers$ = this.procedure$.pipe(
    filter((procedure) => !!procedure),
    map((procedure) => procedure.topModifiers.map(
      (m) => new ProcedureCodeModifier(m),
    )),
    tap((topModifiers) => this.modifiersShown$.next(topModifiers.length > 0 ? 'top' : 'supported')),
  )

  public supportedModifiers$ = this.procedure$
    .pipe(
      filter((procedure) => !!procedure),
      map((procedure) => procedure.supportedModifiers.map(
        (m) => new ProcedureCodeModifier(m),
      )),
    )

  public filteredModifiers$ = combineLatest([
    this.filterControl.valueChanges.pipe(startWith('')),
    this.modifiersShown$,
    this.supportedModifiers$,
    this.topModifiers$,
  ])
    .pipe(
      map(([filter, modifiersShown, supportedModifiers, topModifiers]) => {
        const modifiers = modifiersShown === 'top' ? topModifiers : supportedModifiers

        if (!filter) {
          return [...modifiers]
        }

        const search = filter.replace(/_/g, ' ').toLowerCase()

        return modifiers.filter((m) => m.code === search.toUpperCase() || m.abbreviation.toLowerCase().includes(search))
      }),
    )

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() protected parentForm: NgForm,
    @Optional() protected parentFormGroup: FormGroupDirective,
    protected fm: FocusMonitor,
    protected elRef: ElementRef,
    private restService: ApiService,
  ) {
    super(ngControl, parentForm, parentFormGroup, fm, elRef)
  }

  public ngOnChanges({ code }: SimpleChanges) {
    if (!code || code.currentValue === code.previousValue) {
      return
    }

    this.procedureDataParams$.next(code.currentValue)
  }

  public writeValue(value: string[]) {
    this.value = value
    this.modifiersControl.patchValue(value)
  }

  public onModifierSelected(modifiers: string[]) {
    this.value = modifiers
  }
}
